"use client"

import React, { useEffect, useState, useMemo } from "react"
import { useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import { toast } from "react-toastify"

import HeadingCp from "../components/HeadingCp"
import LoadingSpinner from "../components/LoadingSpinner"

import ElectricalContactBox from "../components/ElectricalContactBox"
import ElectricalSystemItemBox from "../components/ElectricalSystemItemBox"
import ElectricalSelectedItemsBox from "../components/ElectricalSelectedItemsBox"
import ElectricalSectionSummaryBox from "../components/ElectricalSectionSummaryBox"

const initialHeader = {
  id: null,
  employee_name: "",
  project_address: "",
  project_description: "",
  contact_name: "",
  contact_address: "",
  contact_email: "",
  contact_phone: "",
  project_notes: "",
  status: "pending",
  date: "",
}

const ElectricalEstimate = () => {
  const apiUrl = process.env.REACT_APP_API_URL
  const { id } = useParams()        
  const [sectionsDetails, setSectionsDetails] = useState({})
  const [sectionsCalc, setSectionsCalc] = useState({})

  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [savingHeader, setSavingHeader] = useState(false)
  const [savingLines, setSavingLines] = useState(false)

  const [header, setHeader] = useState(initialHeader)
  const [lines, setLines] = useState([])
  const [editingLineId, setEditingLineId] = useState(null)

  const [systemSections, setSystemSections] = useState([])
  const [systemItems, setSystemItems] = useState([])

  const editingLine = useMemo(
    () => lines.find((l) => l.id === editingLineId) || null,
    [lines, editingLineId]
  )

  // ───────────────────────── Fetch sections/items + estimate ─────────────────────────
useEffect(() => {
  const fetchAll = async () => {
    setLoading(true)
    try {
      const [sectionsRes, itemsRes, sectionsDetailRes] = await Promise.all([
        axios.get(`${apiUrl}/electrical_estimate_crud.php`, {
          params: { listSections: 1 },
        }),
        axios.get(`${apiUrl}/electrical_estimate_crud.php`, {
          params: { listItems: 1 },
        }),
        axios.get(`${apiUrl}/electrical_estimate_crud.php`, {
          params: { getSectionsDetail: 1 },
        }),
      ])

      console.log(sectionsDetailRes.data);
      

      setSystemSections(Array.isArray(sectionsRes.data) ? sectionsRes.data : [])
      setSystemItems(Array.isArray(itemsRes.data) ? itemsRes.data : [])

      if (sectionsDetailRes.data && sectionsDetailRes.data.sections) {
        setSectionsDetails(sectionsDetailRes.data.sections)
      } else {
        setSectionsDetails({})
      }

      if (id) {
        await fetchEstimate(id)
      }
    } catch (error) {
      console.error("Error loading electrical estimator meta:", error)
      if (error.response?.status === 401) {
        navigate("/login")
      } else if (error.response?.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: "top-center",
        })
      } else {
        toast.error("Error loading electrical estimator data.", {
          position: "top-center",
        })
      }
    } finally {
      setLoading(false)
    }
  }

  fetchAll()
}, [apiUrl, id, navigate])


  const fetchEstimate = async (estimateId) => {
    const numericId = Number(estimateId)
    if (!numericId) return

    try {
      const res = await axios.get(`${apiUrl}/electrical_estimate_crud.php`, {
        params: { getEstimate: 1, estimate_id: numericId },
      })

      const { header: headerData, lines: lineData, sections_calc } = res.data || {}

      if (headerData) {
        setHeader((prev) => ({
          ...prev,
          ...headerData,
        }))
      }

      if (sections_calc) {
        setSectionsCalc(sections_calc)
      } else {
        setSectionsCalc({})
      }

      if (Array.isArray(lineData)) {
        const mapped = lineData.map((row) => ({
          id: row.id,
          sectionId: row.section_id ?? null,          
          sectionType: row.section_type,
          itemId: row.estimate_system_section_item_id,
          itemLabel: row.item_label,
          units: Number(row.section_system_item_quantity || 0),
        }))
        setLines(mapped)
      }
    } catch (error) {
      console.error("Error fetching estimate:", error)
      if (error.response?.status === 404) {
        toast.error("Estimate not found.", { position: "top-center" })
      } else if (error.response?.status === 401) {
        navigate("/login")
      } else if (error.response?.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: "top-center",
        })
      } else {
        toast.error("Error fetching estimate.", { position: "top-center" })
      }
    }
  }

  // ───────────────────────── Header handlers ─────────────────────────
  const handleHeaderChange = (patch) => {
    setHeader((prev) => ({ ...prev, ...patch }))
  }

  const handleSaveHeader = async () => {
    setSavingHeader(true)
    try {
      const isUpdate = !!header.id
      const status = isUpdate ? "updateEstimateHeader" : "createEstimateHeader"

      const bodyData = {
        status,
        data: {
          id: header.id,
          employee_name: header.employee_name,
          project_address: header.project_address,
          project_description: header.project_description,
          contact_name: header.contact_name,
          contact_address: header.contact_address,
          contact_email: header.contact_email,
          contact_phone: header.contact_phone,
          project_notes: header.project_notes,
        },
      }

      const res = await axios.post(
        `${apiUrl}/electrical_estimate_crud.php`,
        bodyData,
        { headers: { "Content-Type": "application/json" } }
      )

      if (res.data && res.data.success) {
        const newId = res.data.estimate_id || header.id
        setHeader((prev) => ({ ...prev, id: newId }))
        toast.success("Project info saved.", { position: "top-center" })

        // If it was a new estimate and you want URL with ID:
        // if (!id && newId) {
        //   navigate(`/electrical_estimate/${newId}`, { replace: true })
        // }
      } else {
        toast.error("Failed to save project info.", { position: "top-center" })
      }
    } catch (error) {
      console.error("Error saving header:", error)
      if (error.response?.status === 401) {
        navigate("/login")
      } else if (error.response?.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: "top-center",
        })
      } else {
        toast.error("Error saving project info.", { position: "top-center" })
      }
    } finally {
      setSavingHeader(false)
    }
  }

  // ───────────────────────── Line handlers ─────────────────────────
  const handleAddOrUpdateLine = ({ sectionId, itemId, units }) => {
    if (!itemId || units <= 0) return

    const section = systemSections.find((s) => String(s.id) === String(sectionId))
    const item = systemItems.find((i) => String(i.id) === String(itemId))
    if (!item) return

    const sectionType = section ? section.type : (item.section_type || "")

    if (editingLineId) {
      setLines((prev) =>
        prev.map((line) =>
          line.id === editingLineId
            ? {
                ...line,
                sectionId: section ? section.id : line.sectionId,
                sectionType,
                itemId: item.id,
                itemLabel: item.item,
                units,
              }
            : line
        )
      )
      setEditingLineId(null)
    } else {
      const newLine = {
        id: Date.now(),
        sectionId: section ? section.id : null,
        sectionType,
        itemId: item.id,
        itemLabel: item.item,
        units,
      }
      setLines((prev) => [...prev, newLine])
    }
  }

  const handleEditLine = (lineId) => {
    setEditingLineId(lineId)
  }

  const handleCancelEditLine = () => {
    setEditingLineId(null)
  }

  const handleDeleteLine = (lineId) => {
    setLines((prev) => prev.filter((line) => line.id !== lineId))
    if (editingLineId === lineId) {
      setEditingLineId(null)
    }
  }

  const handleSaveLines = async () => {
    if (!header.id) {
      toast.error("Save project info first before saving items.", {
        position: "top-center",
      })
      return
    }

    setSavingLines(true)
    try {
      const linesPayload = lines.map((line) => ({
        estimate_system_section_item_id: line.itemId,
        section_system_item_quantity: line.units,
      }))

      const bodyData = {
        status: "saveEstimateLines",
        estimate_id: header.id,
        lines: linesPayload,
      }

      const res = await axios.post(
        `${apiUrl}/electrical_estimate_crud.php`,
        bodyData,
        { headers: { "Content-Type": "application/json" } }
      )

      if (res.data && res.data.success) {
        toast.success("Items saved.", { position: "top-center" })
        fetchEstimate(header.id)
      } else {
        toast.error("Failed to save items.", { position: "top-center" })
      }
    } catch (error) {
      console.error("Error saving lines:", error)
      if (error.response?.status === 401) {
        navigate("/login")
      } else if (error.response?.status === 403) {
        toast.error("Oops!! you don't have access to this area. Speak to the CFO.", {
          position: "top-center",
        })
      } else {
        toast.error("Error saving items.", { position: "top-center" })
      }
    } finally {
      setSavingLines(false)
    }
  }

  const summaryBySection = useMemo(() => {
    const map = new Map()
    lines.forEach((line) => {
      const sectionId = line.sectionId || null
      const sectionType = line.sectionType || "Unknown"

      // Use sectionId if we have it; otherwise fallback to type
      const key = sectionId !== null ? `id:${sectionId}` : `type:${sectionType}`

      if (!map.has(key)) {
        map.set(key, {
          sectionId,
          sectionType,
          totalUnits: 0,
          linesCount: 0,
        })
      }

      const entry = map.get(key)
      entry.totalUnits += Number(line.units) || 0
      entry.linesCount += 1
    })
    return Array.from(map.values())
  }, [lines])

  return (
    <div className="min-h-screen bg-slate-50 pb-10">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-6">
        <HeadingCp label={id ? "Edit Electrical Estimate" : "New Electrical Estimate"} />
      </div>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-6">
          <button
            type="button"
            onClick={() => navigate("/electrical_estimate_list")}
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            ← Back to List
          </button>
      </div>

      <div className="mx-auto max-w-7xl px-4">
        {loading ? (
          <LoadingSpinner>Loading electrical estimator…</LoadingSpinner>
        ) : (
          <div className="grid gap-4 lg:grid-cols-3">
            <ElectricalContactBox
              header={header}
              onChange={handleHeaderChange}
              onSave={handleSaveHeader}
              saving={savingHeader}
            />

            <ElectricalSystemItemBox
              systemSections={systemSections}
              systemItems={systemItems}
              onAddOrUpdateLine={handleAddOrUpdateLine}
              editingLine={editingLine}
              onCancelEdit={handleCancelEditLine}
            />

            <ElectricalSelectedItemsBox
              lines={lines}
              onEdit={handleEditLine}
              onDelete={handleDeleteLine}
              onSaveLines={handleSaveLines}
              savingLines={savingLines}
            />

            <ElectricalSectionSummaryBox sectionsCalc={sectionsCalc} />

          </div>
        )}
      </div>
    </div>
  )
}

export default ElectricalEstimate
